import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { CATEGORY_LABEL, errMsg, formatRp, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { ProductFormDialog } from "@/pages/admin/ProductFormDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function AdminProducts() {
  const call = useApi();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => call("get", "/admin/products").then(setList), [call]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try { await call("delete", `/admin/products/${toDelete.id}`); toast.success("Produk dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setToDelete(null); }
  };

  const shown = (list || []).filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Katalog Produk">
        <Button data-testid="add-product-button" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="mr-1 h-4 w-4" /> Tambah Produk</Button>
      </PageHeader>
      <Input placeholder="Cari produk..." className="mb-4 max-w-sm bg-white" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-product-search" />
      <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-stone-100 text-left text-xs text-stone-600">
            <tr><th className="p-3">Produk</th><th className="p-3">Kategori</th><th className="p-3 text-right">Harga</th><th className="p-3 text-right">HPP</th><th className="p-3 text-right">Stok</th><th className="p-3 text-right">Terjual</th><th className="p-3">Status</th><th className="p-3" /></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {shown.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                <td className="p-3"><div className="flex items-center gap-3"><img src={imgUrl(p.images?.[0])} alt="" className="h-10 w-10 rounded object-cover" /><span className="font-medium">{p.name}</span></div></td>
                <td className="p-3 text-stone-600">{CATEGORY_LABEL[p.category]}</td>
                <td className="tnum p-3 text-right">{formatRp(p.final_price)}{p.discount_percent > 0 && <span className="block text-xs text-stone-400">-{p.discount_percent}%</span>}</td>
                <td className="tnum p-3 text-right text-stone-600">{formatRp(p.hpp)}</td>
                <td className={`tnum p-3 text-right ${p.stock <= 5 ? "font-semibold text-amber-800" : ""}`}>{p.stock}</td>
                <td className="tnum p-3 text-right">{p.sold || 0}</td>
                <td className="p-3">{p.active ? <span className="text-green-700">Tampil</span> : <span className="text-stone-400">Disembunyikan</span>}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" data-testid={`edit-product-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-800" data-testid={`delete-product-${p.id}`} onClick={() => setToDelete(p)}><Trash2 className="mr-1 h-4 w-4" />Hapus</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list && shown.length === 0 && <p className="p-6 text-center text-sm text-stone-500">Belum ada produk.</p>}
      </div>
      <ProductFormDialog open={open} onOpenChange={setOpen} product={editing} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus produk?</AlertDialogTitle><AlertDialogDescription>"{toDelete?.name}" akan dihapus dari katalog. Riwayat pesanan tetap tersimpan.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-product" onClick={remove}>Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
